/**
 * BuilderContextMenu — Right-click menu for strategy nodes.
 *
 * Operations: Copy, Cut, Paste as Child, Paste as Sibling,
 * Duplicate, Delete, Delete with Children, Remove All Children,
 * Wrap In, Move Up/Down.
 */

import { useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { useBuilderStore, useClipboard } from '../../stores/builderStore';
import { canHaveChildren, findParent, NODE_LABELS } from '../../types/builder';
import type { StrategyNode, NodeType } from '../../types/builder';

interface ContextMenuProps {
  node: StrategyNode;
  x: number;
  y: number;
  onClose: () => void;
  isRoot?: boolean;
}

const WRAP_TYPES: NodeType[] = ['weight-equal', 'weight-specified', 'weight-inverse-volatility', 'group', 'if', 'filter'];

export function BuilderContextMenu({ node, x, y, onClose, isRoot }: ContextMenuProps) {
  const store = useBuilderStore;
  const clipboard = useClipboard();
  const ref = useRef<HTMLDivElement>(null);
  const strategy = useBuilderStore((s) => s.strategy);

  const parentResult = isRoot ? null : findParent(strategy.root, node.id);
  const canMoveUp = parentResult?.key === 'children' && parentResult.index > 0;
  const canMoveDown =
    parentResult?.key === 'children' &&
    'children' in parentResult.parent &&
    parentResult.index < (parentResult.parent as any).children.length - 1;
  const hasClipboard = !!clipboard.node;
  const canPasteChild = hasClipboard && canHaveChildren(node.type);
  const canPasteSibling = hasClipboard && !isRoot && parentResult?.key === 'children';
  const hasChildren =
    ('children' in node && Array.isArray(node.children) && node.children.length > 0) ||
    ('child' in node && !!node.child);

  // Close handlers
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  // Position clamping
  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(x, window.innerWidth - 250),
    top: Math.min(y, window.innerHeight - 400),
    zIndex: 100,
  };

  const action = useCallback(
    (fn: () => void) => () => {
      fn();
      onClose();
    },
    [onClose],
  );

  const Item = ({
    label,
    shortcut,
    onClick,
    disabled,
    destructive,
  }: {
    label: string;
    shortcut?: string;
    onClick: () => void;
    disabled?: boolean;
    destructive?: boolean;
  }) => (
    <button
      disabled={disabled}
      onClick={action(onClick)}
      className={clsx(
        'w-full flex items-center justify-between px-3 py-1.5 text-sm text-left transition-colors',
        disabled ? 'text-surface-600 cursor-not-allowed' : destructive ? 'text-red-400 hover:bg-red-500/10' : 'text-surface-200 hover:bg-surface-700/60',
      )}
    >
      <span>{label}</span>
      {shortcut && <span className="text-[10px] text-surface-500 ml-4">{shortcut}</span>}
    </button>
  );

  const Divider = () => <div className="my-1 border-t border-surface-700" />;

  return (
    <div ref={ref} style={menuStyle}>
      <div className="min-w-[200px] bg-surface-800 border border-surface-600 rounded-xl shadow-2xl py-1.5 overflow-hidden">
        {/* Clipboard */}
        <Item label="Copy" shortcut="Ctrl+C" onClick={() => store.getState().copyNode(node.id)} />
        <Item label="Cut" shortcut="Ctrl+X" onClick={() => store.getState().cutNode(node.id)} disabled={isRoot} />
        <Item label="Paste as Child" shortcut="Ctrl+V" onClick={() => store.getState().pasteNode(node.id)} disabled={!canPasteChild} />
        <Item label="Paste as Sibling" onClick={() => store.getState().pasteAsSibling(node.id)} disabled={!canPasteSibling} />
        <Item label="Duplicate" shortcut="Ctrl+D" onClick={() => store.getState().duplicateNode(node.id)} disabled={isRoot} />

        <Divider />

        {/* Move */}
        <Item label="Move Up" shortcut="Alt+↑" onClick={() => store.getState().moveNodeUp(node.id)} disabled={!canMoveUp} />
        <Item label="Move Down" shortcut="Alt+↓" onClick={() => store.getState().moveNodeDown(node.id)} disabled={!canMoveDown} />

        <Divider />

        {/* Wrap In submenu (inline) */}
        {!isRoot && (
          <>
            <div className="px-3 py-1 text-[10px] font-semibold text-surface-500 uppercase tracking-wider">
              Wrap In
            </div>
            {WRAP_TYPES.map((type) => (
              <Item
                key={type}
                label={`  ${NODE_LABELS[type]}`}
                onClick={() => store.getState().wrapNode(node.id, type)}
              />
            ))}
            <Divider />
          </>
        )}

        {/* Delete */}
        <Item
          label="Remove All Children"
          onClick={() => store.getState().removeAllChildren(node.id)}
          disabled={!hasChildren}
          destructive
        />
        <Item
          label="Delete"
          shortcut="Del"
          onClick={() => store.getState().deleteNode(node.id)}
          disabled={isRoot}
          destructive
        />
      </div>
    </div>
  );
}
